// src/components/Admin/shared/MapConfigEditor.jsx
import React, { useState } from "react";
import { ChevronDown, ChevronRight, Plus, Trash2, Copy } from "lucide-react";
import TierEditor, { DEFAULT_TIERS } from "./TierEditor";

// ============================================
// DEFAULTS BY GEOGRAPHY TYPE
// ============================================

const GEO_DEFAULTS = {
  ZCTA: {
    geographyLabel: "ZIP Code",
    geographyLabelPlural: "ZIP Codes",
    display: {
      geoIdField: "geoId",
      geoJsonIdField: "GEOID20",
      geoJsonIdPrefix: "",
      labelField: "city",
      secondaryLabelField: "zcta",
      rankField: "rank",
      scoreField: "score",
      tierField: "tier",
      maxScore: 100,
    },
    popup: {
      titleField: "city",
      subtitleField: "zcta",
      fields: [
        { key: "county", label: "County" },
        { key: "population", label: "Population", format: "number" },
        { key: "households", label: "Households", format: "number" },
      ],
    },
    details: {
      sections: [
        {
          id: "location",
          title: "Location",
          fields: [
            { key: "city", label: "City" },
            { key: "county", label: "County" },
            { key: "state", label: "State" },
          ],
        },
        {
          id: "demographics",
          title: "Demographics",
          fields: [
            { key: "population", label: "Population", format: "number" },
            { key: "households", label: "Households", format: "number" },
          ],
        },
        { id: "scoreBreakdown", title: "Score Breakdown", type: "breakdown" },
      ],
    },
  },
  BG: {
    geographyLabel: "Block Group",
    geographyLabelPlural: "Block Groups",
    display: {
      geoIdField: "geoId",
      geoJsonIdField: "GEOIDFQ",
      geoJsonIdPrefix: "1500000US",
      labelField: "city",
      secondaryLabelField: "geoId",
      rankField: "rank",
      scoreField: "score",
      tierField: "tier",
      maxScore: 100,
    },
    popup: {
      titleField: "city",
      subtitleField: "geoId",
      fields: [
        { key: "county", label: "County" },
        { key: "population", label: "Population", format: "number" },
        { key: "households", label: "Households", format: "number" },
      ],
    },
    details: {
      sections: [
        {
          id: "location",
          title: "Location",
          fields: [
            { key: "city", label: "City" },
            { key: "county", label: "County" },
            { key: "state", label: "State" },
          ],
        },
        {
          id: "demographics",
          title: "Demographics",
          fields: [
            { key: "population", label: "Population", format: "number" },
            { key: "households", label: "Households", format: "number" },
          ],
        },
        { id: "scoreBreakdown", title: "Score Breakdown", type: "breakdown" },
      ],
    },
  },
};

export { GEO_DEFAULTS };

// ============================================
// COLLAPSIBLE SECTION
// ============================================

const Section = ({ title, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 transition text-left"
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        {title}
      </button>
      {open && <div className="px-3 py-3 space-y-3">{children}</div>}
    </div>
  );
};

// ============================================
// FIELD ROW HELPERS
// ============================================

const FieldRow = ({ label, children, hint, required }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-3 items-start">
    <label className="text-xs font-medium text-gray-600 pt-2">
      {label}
      {required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
    <div className="sm:col-span-2">
      {children}
      {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
    </div>
  </div>
);

const TextInput = ({ value, onChange, placeholder, mono, disabled }) => (
  <input
    type="text"
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    disabled={disabled}
    className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary disabled:bg-gray-100 ${mono ? "font-mono text-xs" : ""}`}
  />
);

const NumberInput = ({ value, onChange, placeholder, min, max }) => (
  <input
    type="number"
    value={value ?? ""}
    onChange={(e) =>
      onChange(e.target.value === "" ? null : Number(e.target.value))
    }
    placeholder={placeholder}
    min={min}
    max={max}
    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary"
  />
);

// ============================================
// FIELDS LIST EDITOR (reused for popup + details)
// ============================================

const FieldsListEditor = ({ fields = [], onChange }) => {
  const updateField = (idx, key, value) => {
    const updated = fields.map((f, i) =>
      i === idx ? { ...f, [key]: value } : f,
    );
    onChange(updated);
  };

  const removeField = (idx) => {
    onChange(fields.filter((_, i) => i !== idx));
  };

  const addField = () => {
    onChange([...fields, { key: "", label: "" }]);
  };

  return (
    <div className="space-y-2">
      {fields.map((field, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <input
            type="text"
            value={field.key || ""}
            onChange={(e) => updateField(idx, "key", e.target.value)}
            placeholder="key"
            className="flex-1 px-2 py-1.5 text-xs font-mono bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30"
          />
          <input
            type="text"
            value={field.label || ""}
            onChange={(e) => updateField(idx, "label", e.target.value)}
            placeholder="Label"
            className="flex-1 px-2 py-1.5 text-sm bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30"
          />
          <select
            value={field.format || ""}
            onChange={(e) => updateField(idx, "format", e.target.value || null)}
            className="w-24 px-2 py-1.5 text-xs bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30"
          >
            <option value="">No fmt</option>
            <option value="number">Number</option>
            <option value="percent">Percent</option>
            <option value="currency">Currency</option>
          </select>
          <button
            type="button"
            onClick={() => removeField(idx)}
            className="p-1 text-gray-400 hover:text-red-500 rounded"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addField}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 border border-dashed border-gray-300 rounded transition"
      >
        <Plus size={12} />
        Add field
      </button>
    </div>
  );
};

// ============================================
// MAIN MAP CONFIG EDITOR
// ============================================

const MapConfigEditor = ({
  map,
  states = [],
  avatars = [],
  onChange,
  onRemove,
}) => {
  const update = (key, value) => onChange({ ...map, [key]: value });
  const updateNested = (parent, key, value) =>
    onChange({ ...map, [parent]: { ...map[parent], [key]: value } });

  // Apply geo-type defaults
  const applyGeoDefaults = (geoType) => {
    const defaults = GEO_DEFAULTS[geoType];
    if (!defaults) return;
    onChange({
      ...map,
      geographyType: geoType,
      geographyLabel: defaults.geographyLabel,
      geographyLabelPlural: defaults.geographyLabelPlural,
      display: { ...defaults.display },
      popup: {
        ...defaults.popup,
        fields: defaults.popup.fields.map((f) => ({ ...f })),
      },
      details: {
        sections: defaults.details.sections.map((s) => ({
          ...s,
          ...(s.fields ? { fields: s.fields.map((f) => ({ ...f })) } : {}),
        })),
      },
    });
  };

  const display = map.display || {};
  const popup = map.popup || {};
  const details = map.details || {};
  const data = map.data || {};

  return (
    <div className="space-y-3">
      {/* ---- BASIC INFO (always visible) ---- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Map ID <span className="text-red-400">*</span>
          </label>
          <TextInput
            value={map.id}
            onChange={(v) => update("id", v)}
            mono
            placeholder="ct_zcta_self_service"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Label
          </label>
          <TextInput
            value={map.label}
            onChange={(v) => update("label", v)}
            placeholder="Self-Service Laundromat"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Avatar <span className="text-red-400">*</span>
          </label>
          <select
            value={map.avatarId || ""}
            onChange={(e) => update("avatarId", e.target.value)}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary"
          >
            <option value="">Select avatar...</option>
            {avatars.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name || a.id}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            State <span className="text-red-400">*</span>
          </label>
          <select
            value={map.stateAbbreviation || ""}
            onChange={(e) => update("stateAbbreviation", e.target.value)}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary"
          >
            <option value="">Select state...</option>
            {states.map((s) => (
              <option key={s.abbreviation} value={s.abbreviation}>
                {s.name} ({s.abbreviation})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Geography Type <span className="text-red-400">*</span>
          </label>
          <div className="flex items-center gap-2">
            <select
              value={map.geographyType || ""}
              onChange={(e) => {
                const geoType = e.target.value;
                if (geoType && GEO_DEFAULTS[geoType]) {
                  // Ask to apply defaults if display isn't configured yet
                  if (!map.display?.geoJsonIdField) {
                    applyGeoDefaults(geoType);
                  } else {
                    update("geographyType", geoType);
                  }
                } else {
                  update("geographyType", geoType);
                }
              }}
              className="flex-1 px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary"
            >
              <option value="">Select...</option>
              <option value="ZCTA">ZCTA (ZIP Codes)</option>
              <option value="BG">BG (Block Groups)</option>
            </select>
            {map.geographyType && GEO_DEFAULTS[map.geographyType] && (
              <button
                type="button"
                onClick={() => applyGeoDefaults(map.geographyType)}
                title="Apply defaults for this geography type"
                className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition whitespace-nowrap"
              >
                Apply defaults
              </button>
            )}
          </div>
        </div>
        <div className="flex items-end gap-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={map.default || false}
              onChange={(e) => update("default", e.target.checked)}
              className="rounded border-gray-300 text-secondary focus:ring-secondary"
            />
            Default map
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Geography Label (singular)
          </label>
          <TextInput
            value={map.geographyLabel}
            onChange={(v) => update("geographyLabel", v)}
            placeholder="ZIP Code"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Geography Label (plural)
          </label>
          <TextInput
            value={map.geographyLabelPlural}
            onChange={(v) => update("geographyLabelPlural", v)}
            placeholder="ZIP Codes"
          />
        </div>
      </div>

      {/* ---- DATA CONFIG ---- */}
      <Section title="Data Config" defaultOpen>
        <FieldRow
          label="Scored Data Path"
          hint="Filename in /clients/{slug}/scored/"
          required
        >
          <TextInput
            value={data.scoredDataPath}
            onChange={(v) => updateNested("data", "scoredDataPath", v)}
            mono
            placeholder="CT_ZCTA_selfService.json"
          />
        </FieldRow>
        <FieldRow
          label="TopoJSON Object Name"
          hint='Object key inside topojson (usually "zcta")'
        >
          <TextInput
            value={data.topoJsonObjectName}
            onChange={(v) => updateNested("data", "topoJsonObjectName", v)}
            mono
            placeholder="zcta"
          />
        </FieldRow>
        <FieldRow
          label="TopoJSON Path"
          hint="Optional. Full Storage path to shared topojson. Leave blank to use default: shared/geojson/{geoType}/{state}.json"
        >
          <TextInput
            value={data.topoJsonPath}
            onChange={(v) => updateNested("data", "topoJsonPath", v || null)}
            mono
            placeholder="shared/geojson/ZCTA/CA_los_angeles.json"
          />
        </FieldRow>
        <FieldRow
          label="Competitors Path"
          hint="Optional. Path relative to /clients/{slug}/ to competitor JSON. Maps sharing the same avatar can use the same file."
        >
          <TextInput
            value={data.competitorsPath}
            onChange={(v) => updateNested("data", "competitorsPath", v || null)}
            mono
            placeholder="competitors/ct_self_service.json"
          />
        </FieldRow>
        <FieldRow
          label="Opportunities Path"
          hint="Optional. Path relative to /clients/{slug}/ to opportunity JSON. Typically shared across all maps (client-wide data)."
        >
          <TextInput
            value={data.opportunitiesPath}
            onChange={(v) =>
              updateNested("data", "opportunitiesPath", v || null)
            }
            mono
            placeholder="opportunities/ct_opportunities.json"
          />
        </FieldRow>
      </Section>

      {/* ---- DISPLAY FIELDS ---- */}
      <Section title="Display Fields">
        <FieldRow label="Geo ID Field">
          <TextInput
            value={display.geoIdField}
            onChange={(v) => updateNested("display", "geoIdField", v)}
            mono
            placeholder="geoId"
          />
        </FieldRow>
        <FieldRow
          label="GeoJSON ID Field"
          hint="Property in GeoJSON features to match against"
        >
          <TextInput
            value={display.geoJsonIdField}
            onChange={(v) => updateNested("display", "geoJsonIdField", v)}
            mono
            placeholder="GEOID20"
          />
        </FieldRow>
        <FieldRow
          label="GeoJSON ID Prefix"
          hint="Prefix to prepend to geo IDs for matching (leave empty for none)"
        >
          <TextInput
            value={display.geoJsonIdPrefix}
            onChange={(v) =>
              updateNested("display", "geoJsonIdPrefix", v || null)
            }
            mono
            placeholder="1500000US"
          />
        </FieldRow>
        <FieldRow label="Label Field">
          <TextInput
            value={display.labelField}
            onChange={(v) => updateNested("display", "labelField", v)}
            mono
            placeholder="city"
          />
        </FieldRow>
        <FieldRow label="Secondary Label Field">
          <TextInput
            value={display.secondaryLabelField}
            onChange={(v) => updateNested("display", "secondaryLabelField", v)}
            mono
            placeholder="zcta"
          />
        </FieldRow>
        <FieldRow label="Rank Field">
          <TextInput
            value={display.rankField}
            onChange={(v) => updateNested("display", "rankField", v)}
            mono
            placeholder="rank"
          />
        </FieldRow>
        <FieldRow label="Score Field">
          <TextInput
            value={display.scoreField}
            onChange={(v) => updateNested("display", "scoreField", v)}
            mono
            placeholder="score"
          />
        </FieldRow>
        <FieldRow label="Tier Field">
          <TextInput
            value={display.tierField}
            onChange={(v) => updateNested("display", "tierField", v)}
            mono
            placeholder="tier"
          />
        </FieldRow>
        <FieldRow label="Max Score">
          <NumberInput
            value={display.maxScore}
            onChange={(v) => updateNested("display", "maxScore", v)}
            placeholder="100"
            min={0}
          />
        </FieldRow>
      </Section>

      {/* ---- POPUP CONFIG ---- */}
      <Section title="Popup Config">
        <FieldRow label="Title Field">
          <TextInput
            value={popup.titleField}
            onChange={(v) => updateNested("popup", "titleField", v)}
            mono
            placeholder="city"
          />
        </FieldRow>
        <FieldRow label="Subtitle Field">
          <TextInput
            value={popup.subtitleField}
            onChange={(v) => updateNested("popup", "subtitleField", v)}
            mono
            placeholder="zcta"
          />
        </FieldRow>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">
            Popup Fields
          </label>
          <FieldsListEditor
            fields={popup.fields || []}
            onChange={(fields) =>
              onChange({ ...map, popup: { ...popup, fields } })
            }
          />
        </div>
      </Section>

      {/* ---- DETAILS CONFIG ---- */}
      <Section title="Details / Sidebar Config">
        <DetailsSectionsEditor
          sections={details.sections || []}
          onChange={(sections) =>
            onChange({ ...map, details: { ...details, sections } })
          }
        />
      </Section>

      {/* ---- TIER CUTOFFS ---- */}
      <Section title="Tier Cutoffs">
        <TierEditor
          tierCutoffs={map.tierCutoffs || DEFAULT_TIERS}
          onChange={(tierCutoffs) => update("tierCutoffs", tierCutoffs)}
        />
      </Section>

      {/* ---- MAP REPORTS (informational only — uploads in Files tab) ---- */}
      <Section title="Reports Config">
        <ReportsConfigEditor
          reports={map.reports || {}}
          onChange={(reports) => update("reports", reports)}
          mapId={map.id}
        />
      </Section>
    </div>
  );
};

// ============================================
// DETAILS SECTIONS EDITOR
// ============================================

const DetailsSectionsEditor = ({ sections = [], onChange }) => {
  const updateSection = (idx, patch) => {
    onChange(sections.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const removeSection = (idx) => {
    onChange(sections.filter((_, i) => i !== idx));
  };

  const addSection = (type = "fields") => {
    const newSection =
      type === "breakdown"
        ? {
            id: "scoreBreakdown",
            title: "Score Breakdown",
            type: "breakdown",
          }
        : {
            id: `section_${Date.now().toString(36)}`,
            title: "New Section",
            fields: [],
          };
    onChange([...sections, newSection]);
  };

  return (
    <div className="space-y-3">
      {sections.map((section, idx) => {
        const isBreakdown = section.type === "breakdown";
        return (
          <div
            key={idx}
            className="border border-gray-200 rounded-lg p-3 bg-white"
          >
            <div className="flex items-center gap-2 mb-2">
              <input
                type="text"
                value={section.id || ""}
                onChange={(e) => updateSection(idx, { id: e.target.value })}
                placeholder="sectionId"
                className="w-32 px-2 py-1 text-xs font-mono border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30"
              />
              <input
                type="text"
                value={section.title || ""}
                onChange={(e) => updateSection(idx, { title: e.target.value })}
                placeholder="Section Title"
                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30"
              />
              <select
                value={isBreakdown ? "breakdown" : "fields"}
                onChange={(e) => {
                  if (e.target.value === "breakdown") {
                    // Rebuild section without the fields key
                    onChange(
                      sections.map((s, i) => {
                        if (i !== idx) return s;
                        const { fields, ...rest } = s;
                        return { ...rest, type: "breakdown" };
                      }),
                    );
                  } else {
                    const { type, ...rest } = section;
                    updateSection(idx, {
                      ...rest,
                      fields: section.fields || [],
                    });
                  }
                }}
                className="w-28 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none"
              >
                <option value="fields">Fields</option>
                <option value="breakdown">Breakdown</option>
              </select>
              <button
                type="button"
                onClick={() => removeSection(idx)}
                className="p-1 text-gray-400 hover:text-red-500 rounded"
              >
                <Trash2 size={14} />
              </button>
            </div>

            {!isBreakdown && (
              <FieldsListEditor
                fields={section.fields || []}
                onChange={(fields) => updateSection(idx, { fields })}
              />
            )}
            {isBreakdown && (
              <p className="text-xs text-gray-400 italic">
                Score breakdown section — rendered automatically from scored
                data
              </p>
            )}
          </div>
        );
      })}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => addSection("fields")}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 border border-dashed border-gray-300 rounded transition"
        >
          <Plus size={12} />
          Add section
        </button>
        <button
          type="button"
          onClick={() => addSection("breakdown")}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 border border-dashed border-gray-300 rounded transition"
        >
          <Plus size={12} />
          Add breakdown
        </button>
      </div>
    </div>
  );
};

// ============================================
// REPORTS CONFIG EDITOR (paths + metadata)
// ============================================

const REPORT_TYPES = [
  { key: "rankedPdf", label: "Ranked PDF", icon: "file-text", ext: ".pdf" },
  { key: "rankedXlsx", label: "Ranked Excel", icon: "table", ext: ".xlsx" },
  {
    key: "executiveSummary",
    label: "Executive Summary",
    icon: "file-text",
    ext: ".pdf",
  },
  {
    key: "competitorsPdf",
    label: "Competitors PDF",
    icon: "file-text",
    ext: ".pdf",
  },
  {
    key: "competitorsXlsx",
    label: "Competitors Excel",
    icon: "table",
    ext: ".xlsx",
  },
  {
    key: "opportunitiesPdf",
    label: "Opportunities PDF",
    icon: "file-text",
    ext: ".pdf",
  },
  {
    key: "opportunitiesXlsx",
    label: "Opportunities Excel",
    icon: "table",
    ext: ".xlsx",
  },
];

const ReportsConfigEditor = ({ reports = {}, onChange, mapId }) => {
  const updateReport = (key, field, value) => {
    onChange({
      ...reports,
      [key]: { ...reports[key], [field]: value },
    });
  };

  const addReport = (key) => {
    const type = REPORT_TYPES.find((t) => t.key === key);
    onChange({
      ...reports,
      [key]: {
        title: `${type?.label || key} — ${mapId || "map"}`,
        fileName: "",
        storagePath: "",
        icon: type?.icon || "file-text",
      },
    });
  };

  const removeReport = (key) => {
    const updated = { ...reports };
    delete updated[key];
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">
        Configure report metadata. Actual files are uploaded in the Files tab.
      </p>
      {REPORT_TYPES.map(({ key, label, ext }) => {
        const report = reports[key];
        if (!report) {
          return (
            <button
              key={key}
              type="button"
              onClick={() => addReport(key)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-500 hover:text-gray-700 border border-dashed border-gray-300 rounded transition mr-2"
            >
              <Plus size={12} />
              {label}
            </button>
          );
        }
        return (
          <div key={key} className="bg-gray-50 rounded-lg p-2.5 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-600">{label}</span>
              <button
                type="button"
                onClick={() => removeReport(key)}
                className="p-1 text-gray-400 hover:text-red-500 rounded"
              >
                <Trash2 size={12} />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                type="text"
                value={report.title || ""}
                onChange={(e) => updateReport(key, "title", e.target.value)}
                placeholder="Display title"
                className="px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30"
              />
              <input
                type="text"
                value={report.fileName || ""}
                onChange={(e) => updateReport(key, "fileName", e.target.value)}
                placeholder={`filename${ext}`}
                className="px-2 py-1.5 text-xs font-mono border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30"
              />
            </div>
            <input
              type="text"
              value={report.storagePath || ""}
              onChange={(e) => updateReport(key, "storagePath", e.target.value)}
              placeholder={`reports/filename${ext}`}
              className="w-full px-2 py-1.5 text-xs font-mono border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30"
            />
          </div>
        );
      })}
    </div>
  );
};

export default MapConfigEditor;
